// Focused-cell fan (Positions D5 / S3. Cell → fan → act).
//
// Cells are the interactive unit of the board: clicking ANY occupied cell
// FOCUSES it. Its card meshes fly out of the resting stack into a lifted fan
// (layoutCellFan) where each card is individually visible and hittable with
// ALL of its live presentation intact: tap rotation, statuses, stat badges,
// equipment overlays, foils. ESC / clicking elsewhere collapses the fan and
// the cards fly back.
//
// This is NOT the old `_fanned`-flag fan. The board pose is a pure function
// of (server state, focusedCell): the position fold routes a focused cell's
// members to their fan slots instead of their resting cascade slots, so a
// server diff mid-fan folds INTO the fan (a member that dies flies out, an
// arrival fans in) and collapse is just "recompute with focusedCell = null".
// There are no saved poses and no per-frame exemption flags anywhere.
//
// Fly-out/back is a cosmetic XZ glide (glideMeshXZ / tickPoseGlides); the
// vertical rise/fall falls out of the existing Y tween in applyComputedY,
// which targets FAN_Y for focused members.

import * as THREE from 'three';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { layoutCellFanEx, sortCellMembers, FAN_Y } from '../../../utils/game/cellRestLayout';
import { sampleCellGeometry } from '../../../utils/game/engine/core';
import { moveKinematicBody } from '../../../utils/game/physicsWorld';
import { pushDismiss, removeDismiss } from '../../../utils/escStack';
import { UI, playUI } from '../../../utils/arena/uiSounds';

const GLIDE_MS = 220;
const GLIDE_SNAP_DIST = 0.05;

export function cellKeyForCard(card) {
  const col = card?.cell?.col;
  const row = card?.cell?.row;
  return typeof col === 'number' && typeof row === 'number' ? `${col},${row}` : null;
}

// A mesh's cell: the authoritative stamp when present, else resolved from its
// world position (a dealt avatar or in-flight cast that predates its first
// position fold).
export function meshCellKey(host, mesh, card) {
  const stamped = cellKeyForCard(card);
  if (stamped) return stamped;
  if (typeof host._cellAtWorld === 'function' && mesh?.position) {
    const at = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (at) return `${at.col},${at.row}`;
  }
  return null;
}

// The focused cell's member list, derived fresh from live card state on every
// call. Auras are excluded. They span four cells from an intersection anchor.
export function focusMembersForCell(host, cellKey) {
  if (!cellKey) return [];
  const entries = [];
  for (const [id, mesh] of host.meshes) {
    if (mesh?.userData?.type !== 'card') continue;
    const card = mesh.userData.cardInstance;
    if (!card || card.type === 'Aura') continue;
    if (meshCellKey(host, mesh, card) !== cellKey) continue;
    entries.push({
      id,
      mesh,
      card,
      isSite: !!card.isSite,
      isToken: !!card.isToken,
      tapped: !!card.tapped,
      subsurface: !!card.subsurface,
      stackSeq: card.stackSeq,
    });
  }
  return sortCellMembers(entries, host.state.regionView === 'subsurface');
}

export function isCellFocused(host, cellKey) {
  return !!cellKey && host.state.focusedCell === cellKey;
}

export function isFocusMember(host, id) {
  const key = host.state.focusedCell;
  if (!key) return false;
  return focusMembersForCell(host, key).some((m) => m.id === id);
}

// Fan poses for the currently focused cell. Local presentation: the bow/row
// direction follows the local viewer's orientation, never synced.
export function fanPosesForFocusedCell(host) {
  const cellKey = host.state.focusedCell;
  if (!cellKey) {
    host._fanLayoutMeta = null;
    return new Map();
  }
  const grid = getGameGrid(host.state?.spawnConfig);
  if (!grid) return new Map();
  const [colStr, rowStr] = cellKey.split(',');
  const geo = sampleCellGeometry(grid, parseInt(colStr, 10), parseInt(rowStr, 10));
  if (!geo) return new Map();
  const { poses, meta } = layoutCellFanEx(geo, focusMembersForCell(host, cellKey), {
    subsurfaceView: host.state.regionView === 'subsurface',
    rotated: host._localCameraRotated?.() ?? false,
  });
  host._fanLayoutMeta = meta;
  return poses;
}

// ── Open / close ───────────────────────────────────────────────────────────

export function openCellFocus(host, cellKey) {
  if (!cellKey) return false;
  if (isCellFocused(host, cellKey)) return true;
  if (focusMembersForCell(host, cellKey).length === 0) return false;
  if (host.state.focusedCell) closeCellFocus(host, { silent: true });
  host._cellFocusEsc = pushDismiss(() => closeCellFocus(host));
  host.setState({ focusedCell: cellKey }, () => applyCellFocusPoses(host));
  playUI(UI.CLICK);
  return true;
}

export function closeCellFocus(host, { silent = false } = {}) {
  if (!host.state.focusedCell) return;
  if (host._cellFocusEsc) {
    removeDismiss(host._cellFocusEsc);
    host._cellFocusEsc = null;
  }
  host._fanLayoutMeta = null;
  removeFanScrim(host);
  host.setState({ focusedCell: null }, () => {
    // Fly back: re-fold every board pose to server truth. The fold glides
    // members to their resting cascade slots; the Y tween drops them.
    host._refoldPositions?.();
  });
  if (!silent) playUI(UI.CANCEL);
}

export function toggleCellFocus(host, cellKey) {
  if (isCellFocused(host, cellKey)) closeCellFocus(host);
  else openCellFocus(host, cellKey);
}

// Fold hook: board state changed under the fan. Card-target prompts and
// combat selection keep the fan OPEN. Candidate halos anchor to the mesh
// poses, so a fanned candidate is exactly as clickable as a resting one,
// and a crowded square is where the fan helps most. Only CELL-pick flows
// collapse it (a click means "this cell" there, and the fan's outside-click
// consume would eat it). An emptied cell collapses. Otherwise re-assert the
// fan poses so membership churn (arrival / death / move-away) re-lays it.
export function refreshCellFocus(host) {
  if (!host.state.focusedCell) return;
  if (host.state.pendingServerChoice?.kind === 'cell' || host.state.pendingCellTarget
    || host.state.pendingSummonPlacement) {
    closeCellFocus(host, { silent: true });
    return;
  }
  if (focusMembersForCell(host, host.state.focusedCell).length === 0) {
    closeCellFocus(host, { silent: true });
    return;
  }
  applyCellFocusPoses(host);
}

// Glide every focused member to its fan slot. Skips members under an active
// drag (the cursor owns their pose). Stamps card.x/z with the fan target.
// the folds do the same, and the collapse refold's "already in place"
// short-circuit compares against card.x/z, so a stale rest value there
// would strand the mesh at its fan slot.
export function applyCellFocusPoses(host) {
  const poses = fanPosesForFocusedCell(host);
  if (!poses.size) return;
  for (const [id, pose] of poses) {
    if (host.dragging?.draggingIds?.has(id)) continue;
    const mesh = host.meshes.get(id);
    if (!mesh) continue;
    glideMeshXZ(host, mesh, pose.x, pose.z);
    const card = mesh.userData?.cardInstance;
    if (card) {
      card.x = pose.x;
      card.z = pose.z;
    }
  }
  syncFanScrim(host);
}

// ── Fan scrim + chrome ─────────────────────────────────────────────────────
// A soft dark drop-shadow plane floats just under the fan so the spread
// reads as its own interface layer above the board. Pure presentation.
// non-raycastable, added/removed with the fan, sized from the layout meta.

const SCRIM_Y_DROP = 0.5;
const SCRIM_PAD = 2.2; // world-units of soft falloff beyond the fan bbox

let scrimTexture = null;
function getScrimTexture() {
  if (scrimTexture || typeof document === 'undefined') return scrimTexture;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(128, 128, 40, 128, 128, 128);
  g.addColorStop(0, 'rgba(0,0,0,0.62)');
  g.addColorStop(0.72, 'rgba(0,0,0,0.5)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  scrimTexture = new THREE.CanvasTexture(c);
  return scrimTexture;
}

function syncFanScrim(host) {
  const meta = host._fanLayoutMeta;
  const scene = host.scene?.scene;
  if (!meta || !scene) {
    removeFanScrim(host);
    return;
  }
  if (!host._fanScrimMesh) {
    const tex = getScrimTexture();
    if (!tex) return;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: tex, transparent: true, depthWrite: false,
      }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.raycast = () => {}; // never intercept card/board picking
    scene.add(mesh);
    host._fanScrimMesh = mesh;
  }
  const m = host._fanScrimMesh;
  m.position.set(meta.cx, FAN_Y - SCRIM_Y_DROP, (meta.top + meta.bottom) / 2);
  m.scale.set(
    meta.width + SCRIM_PAD * 2,
    Math.abs(meta.bottom - meta.top) + SCRIM_PAD * 2,
    1,
  );
}

export function removeFanScrim(host) {
  const mesh = host._fanScrimMesh;
  if (!mesh) return;
  mesh.parent?.remove(mesh);
  mesh.geometry?.dispose();
  mesh.material?.dispose();
  host._fanScrimMesh = null;
}

// Per-frame projector for the fan chrome DOM overlay (divider + region
// headings, CellFanChrome.jsx). Elements register via host.fanChromeRefs
// (Map name -> el): 'divider', 'surfaceLabel', 'subsurfaceLabel'.
export function updateCellFanChrome(host) {
  const refs = host.fanChromeRefs;
  if (!refs || refs.size === 0) return;
  const meta = host._fanLayoutMeta;
  const camera = host.scene?.camera;
  const canvas = host.canvasRef?.current;
  const hide = () => { for (const el of refs.values()) el.style.display = 'none'; };
  if (!meta || !meta.hasBoth || !camera || !canvas || !host.state.focusedCell) {
    hide();
    return;
  }
  const rect = canvas.getBoundingClientRect();
  const v = host._hugVec;
  const project = (x, z) => {
    v.set(x, FAN_Y, z).project(camera);
    return { x: ((v.x + 1) / 2) * rect.width, y: ((-v.y + 1) / 2) * rect.height };
  };
  const halfW = meta.width / 2 + 0.6;
  const divider = refs.get('divider');
  if (divider) {
    const a = project(meta.cx - halfW, meta.dividerZ);
    const b = project(meta.cx + halfW, meta.dividerZ);
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    divider.style.display = 'block';
    divider.style.width = `${Math.round(len)}px`;
    divider.style.transform = `translate(${Math.round(a.x)}px, ${Math.round(a.y)}px) rotate(${ang}rad)`;
  }
  const placeLabel = (name, z) => {
    const el = refs.get(name);
    if (!el) return;
    if (z == null) { el.style.display = 'none'; return; }
    const p = project(meta.cx - halfW, z);
    el.style.display = 'block';
    el.style.transform = `translate(${Math.round(p.x)}px, ${Math.round(p.y)}px) translateY(-100%)`;
  };
  placeLabel('surfaceLabel', meta.surfaceTopZ);
  placeLabel('subsurfaceLabel', meta.subsurfaceTopZ);
}

// ── XZ glide engine ────────────────────────────────────────────────────────
// Cosmetic in-plane motion for fold-driven pose changes: fan out/in, cascade
// re-slots, server-confirmed moves. Y stays owned by applyComputedY's tween.

export function glideMeshXZ(host, mesh, x, z, dur = GLIDE_MS) {
  const dx = x - mesh.position.x;
  const dz = z - mesh.position.z;
  // Snap when the hop is imperceptible. And ALWAYS in a headless host
  // (unit tests, the e2e testkit): no render loop ever ticks the glide
  // there, so an animated pose would simply never arrive.
  const animated = typeof host.scene?.onFrame === 'function';
  if (!animated || Math.hypot(dx, dz) < GLIDE_SNAP_DIST) {
    delete mesh.userData._glideXZ;
    setMeshXZ(mesh, x, z);
    return;
  }
  mesh.userData._glideXZ = {
    sx: mesh.position.x, sz: mesh.position.z, tx: x, tz: z,
    t0: performance.now(), dur,
  };
}

function setMeshXZ(mesh, x, z) {
  mesh.position.x = x;
  mesh.position.z = z;
  const body = mesh.userData?.body;
  if (body) moveKinematicBody(body, x, mesh.position.y, z);
}

// Per-frame: advance all live glides (ease-out cubic). A card the player
// picks up mid-glide is released to the drag.
export function tickPoseGlides(host) {
  const now = performance.now();
  for (const [id, mesh] of host.meshes) {
    const g = mesh.userData?._glideXZ;
    if (!g) continue;
    if (host.dragging?.draggingIds?.has(id)) {
      delete mesh.userData._glideXZ;
      continue;
    }
    const t = Math.min(1, (now - g.t0) / g.dur);
    const e = 1 - (1 - t) ** 3;
    setMeshXZ(mesh, g.sx + (g.tx - g.sx) * e, g.sz + (g.tz - g.sz) * e);
    if (t >= 1) delete mesh.userData._glideXZ;
  }
}

// ── Board click routing ────────────────────────────────────────────────────

// Entry point from handleMouseDown once every modal interaction has had its
// shot. The fan is OPT-IN: a plain click keeps the classic per-card
// interaction; HOLDING the expand hotkey (`board.expandSquare`, default E)
// enters square-selection mode and clicking an occupied square fans it
// open. The per-square expand button (CellStackBadges) is the mouse-only
// path to the same openCellFocus. Returns:
//   'member'. The click landed on a member of the OPEN fan: the caller
//               falls through to the classic card interaction (drag
//               candidate → menu/drag), which is the whole point of the fan.
//   true. The click was consumed (a fan opened, switched, or closed).
//   false. Not a fan gesture: the caller runs its classic handling.
export function routeBoardClickToFocus(host, event, hit) {
  const squareMode = !!host.squareMode;
  const card = hit?.userData?.cardInstance;
  let cellKey = card ? meshCellKey(host, hit, card) : null;
  if (card && cellKey && isCellFocused(host, cellKey)) return 'member';
  if (!cellKey) {
    const point = host.scene?.raycastTablePoint?.(event);
    if (point) {
      const at = host._cellAtWorld?.(point.x, point.z);
      if (at) cellKey = `${at.col},${at.row}`;
    }
  }

  // Fan open, click landed elsewhere: square mode switches to the other
  // square; a plain click anywhere outside the fan collapses it (and is
  // consumed. An exit click must never accidentally pick up a card
  // underneath).
  if (host.state.focusedCell) {
    if (squareMode && cellKey && focusMembersForCell(host, cellKey).length > 0) {
      openCellFocus(host, cellKey);
      return true;
    }
    closeCellFocus(host);
    return true;
  }

  // Fan closed: only a square-mode click on an occupied square opens it.
  if (squareMode && cellKey && focusMembersForCell(host, cellKey).length > 0) {
    openCellFocus(host, cellKey);
    return true;
  }
  return false;
}

// ── Hover highlight ────────────────────────────────────────────────────────

// Per-frame: soft glow quad over the occupied cell under the cursor. The
// square-selection-mode affordance. Fan mode is opt-in, so the highlight
// shows only while the expand hotkey is held, and NEVER over the focused
// square itself: the quad is a DOM overlay above the WebGL canvas, so it
// would paint on top of the fanned cards. Hidden during drags, picks, and
// combat selection (their own overlays own the board then).
export function updateCellHoverHighlight(host) {
  const el = host._cellHoverHighlightRef;
  if (!el) return;
  const hide = () => { el.style.display = 'none'; };
  if (host.dragging || host.state.pendingCellTarget || host.state.pendingSummonPlacement
    || host.state.pendingServerChoice?.kind === 'cell') return hide();
  if (!host.lastMouseEvent || !host.scene?.camera || !host.canvasRef?.current) return hide();
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return hide();
  const point = host.scene.raycastTablePoint(host.lastMouseEvent);
  if (!point) return hide();
  const cell = host._cellAtWorld(point.x, point.z);
  if (!cell) return hide();
  const key = `${cell.col},${cell.row}`;
  if (!host.squareMode || isCellFocused(host, key)) return hide();
  if (focusMembersForCell(host, key).length === 0) return hide();
  const rect = host.canvasRef.current.getBoundingClientRect();
  const projected = host._projectCellQuad(grid, cell.col, cell.row, host.scene.camera, rect);
  if (!projected) return hide();
  host._applyCellQuadStyle(el, projected);
}
